/**
 * SharePoint site connect — accountPortalCompanyApi routes.
 * @see backend/accountPortal/integrations/sharepoint.py
 */

import { getStoredAccessToken } from './accountPortalClient.js';

const API_BASE = (import.meta.env.VITE_ACCOUNT_PORTAL_API_URL || '').replace(/\/$/, '');

function requireBase() {
  if (!API_BASE) {
    throw new Error('VITE_ACCOUNT_PORTAL_API_URL is not configured');
  }
}

async function request(path, { method = 'GET', body } = {}) {
  requireBase();
  const token = getStoredAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body != null ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const detail = data?.detail;
    const message =
      (typeof detail === 'string' && detail)
      || detail?.message
      || data?.message
      || `Request failed (${res.status})`;
    throw Object.assign(new Error(message), {
      status: res.status,
      code: data?.code || detail?.code,
      data,
    });
  }
  if (data?.code === 'MS_ADMIN_CONSENT_REQUIRED') {
    throw Object.assign(new Error('Microsoft admin sign-in required'), {
      status: res.status,
      code: data.code,
      data,
    });
  }
  return data;
}

export function isSharepointSitesApiAvailable() {
  return Boolean(API_BASE);
}

export async function previewSite(companyId, url) {
  const q = new URLSearchParams({
    companyId: String(companyId || ''),
    url: String(url || '').trim(),
  });
  return request(`/accountPortal/integrations/sharepoint/preview?${q}`);
}

export async function connectSite(companyId, url) {
  return request('/accountPortal/integrations/sharepoint/connect', {
    method: 'POST',
    body: { companyId, url: String(url || '').trim() },
  });
}

export async function listConnectedSites(companyId) {
  const q = new URLSearchParams({ companyId: String(companyId || '') });
  const data = await request(`/accountPortal/integrations/sharepoint/sites?${q}`);
  return data?.sites || data?.items || [];
}

export async function getAdminConsentUrl() {
  const data = await request('/accountPortal/integrations/sharepoint/admin-consent-url');
  return data?.consentUrl || '';
}